import { useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { QuickFilterIcon } from "@acto/web-common/components";
import { IconButton } from "@acto/taskpane/field-sales-2/components/primitives/IconButton";
import { FilterPopover, type FilterPopoverGroup } from "./FilterPopover";

// Mirrors Search's inline filter-button decoration so a standalone quick filter trigger reads
// consistently with the search pill's filter trigger, without reaching into that component's
// private styles.
const FilterTriggerIconBox = styled("span")(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
}));

const ActiveFilterDot = styled("span")(({ theme }) => ({
  position: "absolute",
  border: "1px solid white",
  top: "1px",
  right: "-1px",
  width: "8px",
  height: "8px",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.accent.action,
  pointerEvents: "none",
}));

export interface QuickFilterTriggerProps {
  groups: FilterPopoverGroup[];
  selectedKeys: string[];
  onToggle: (key: string) => void;
  /** Shows the active-state dot on the trigger. Decided by the caller (e.g. "all" selected = inactive). */
  isActive: boolean;
  ariaLabel?: string;
  className?: string;
}

/**
 * Presentational primitive for a standalone quick-filter trigger: an icon button (with an
 * active-state dot) that anchors a [FilterPopover]. Owns only the open/close state and the anchor
 * ref — the selected filter state itself is modeled per-page (customer vs. product quick filters
 * differ deliberately in how "all" is represented) and passed in via props.
 *
 * Not used by [TopBar]/[Search]'s filter trigger: there the button lives inside the search pill
 * (with bare/expanded layout states) while the popover is rendered by TopBar itself, a materially
 * different composition than "button + popover together".
 */
export function QuickFilterTrigger({
  groups,
  selectedKeys,
  onToggle,
  isActive,
  ariaLabel,
  className,
}: QuickFilterTriggerProps) {
  const t = useTranslations("fieldSalesTwo.search");
  const resolvedAriaLabel = ariaLabel ?? t("filter");
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label={resolvedAriaLabel}
        className={className}
        onClick={() => setIsOpen((v) => !v)}
      >
        <FilterTriggerIconBox>
          <QuickFilterIcon />
          {isActive && <ActiveFilterDot aria-hidden="true" />}
        </FilterTriggerIconBox>
      </IconButton>
      <FilterPopover
        anchorRef={anchorRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        groups={groups}
        selectedKeys={selectedKeys}
        onToggle={onToggle}
      />
    </>
  );
}
